import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Redirect, Route, Switch, useLocation} from 'react-router-dom';
import Homepage from '../HomePage';
import ProgramPage from '../programme/ProgramPage';
import BallroomPage from '../ballroom/BallroomPage';
import ConversationPage from '../ConversationPage';
import SocialWallPage from '../SocialWallPage';
import PhotoBoothPage from '../photobooth/PhotoBoothPage';
import FeedbackPage from '../FeedbackPage';
import FAQPage from '../FAQPage';
import AppHeader from '../../components/appHeader/AppHeader';
import AppFooter from '../../components/appFooter/AppFooter';
import ProgramHeader from '../../components/programHeader/ProgramHeader';
import PrivateRoute from '../../components/PrivateRoute';
import '../../App.css';
import LogoutPage from '../Logout';
import 'firebase/firestore';
import 'firebase/auth';
import 'firebase/analytics';
import {config, FrontendRoutes} from '../../constants';
import {useCollectionData} from 'react-firebase-hooks/firestore';
import {usersRef} from '../../config/firebaseConfig';
import {encodeToFirebase, getBackground, getBgUrl, updateHideStatus} from './helpers';
import PlayerLoadingWrapper from './PlayerLoadingWrapper';
import useIsSpecial from '../../hooks/specialRoute';
import {
	addUserIfNotExists,
	fetchAndListenHideStatus,
	onlineUserFetching,
	updateOnlineStatus,
	updateOnlineStatusPeriodically
} from './effects';
import PortraitWarning from './PortraitWarning';
import {useAuth} from '../../hooks/useAuth';
import SocialWallModerationPage from '../admin/SocialWallModerationPage';
import QAModerationPage from '../admin/QAModerationPage';
import {makeStyles} from '@material-ui/core/styles';
import AfterEventMessage from '../AfterEventMessage';
import ReportsPage from '../admin/ReportsPage';

const useStyles = makeStyles({
	root: {
		textAlign: 'center',
		position: 'absolute',
		backgroundImage: `url(${getBackground(FrontendRoutes.COUNTDOWN)})`,
		backgroundRepeat: 'no-repeat',
		backgroundSize: '100% 101%',
		backgroundPosition: 'center right',
		minWidth: '100%',
		minHeight: '100%'
	},
	ballroom: {
		backgroundImage: `url(${getBackground(FrontendRoutes.BALLROOM())})`,
	},
	conversation: {
		backgroundImage: `url(${getBackground(FrontendRoutes.CONVERSATION)})`,
	},
	programme: {
		backgroundImage: `url(${getBackground(FrontendRoutes.PROGRAMME)})`,
	},
	photobooth: {
		backgroundImage: `url(${getBackground(FrontendRoutes.PHOTO_BOOTH)})`,
	},
	socialwall: {
		backgroundImage: `url(${getBackground(FrontendRoutes.SOCIAL_WALL)})`,
	},
	logout: {
		backgroundImage: `url(${getBackground(FrontendRoutes.LOGOUT)})`,
	},
});

const App = () => {

	const location = useLocation();
	const path = location.pathname;

	const isSpecial = useIsSpecial();
	const [userAdded, setUserAdded] = useState(false);
	const [landScapeMode, setLandScapeMode] = React.useState(true);
	const [imgLoading, setImageLoading] = React.useState(true);

	const classes = useStyles({pathname: location.pathname});

	const {
		data: {user},
		isError,
		isLoading: authLoading,
	} = useAuth();

	const [users] = useCollectionData(usersRef);
	const [onlineUserIds, setOnlineUserIds] = useState([]);

	const [hideStatus, setHideStatus] = useState(false);

	const handleHideStatus = useCallback(hide => {
		updateHideStatus(user, hide);
		setHideStatus(hide);
	}, [user]);

	const onlineUsers = useMemo(() => users ?
		users.filter(u =>
			onlineUserIds.includes(encodeToFirebase(u.CustID + ''))
		) : null, [users, onlineUserIds]);


	useEffect(fetchAndListenHideStatus(user, setHideStatus), [user]);

	useEffect(onlineUserFetching(setOnlineUserIds), []);

	useEffect(addUserIfNotExists(user, users, userAdded, setUserAdded), [user, users, userAdded]);

	useEffect(updateOnlineStatusPeriodically(user, hideStatus), [user, hideStatus]);

	const backgroundClass = () => {
		if (path === FrontendRoutes.CONVERSATION)
			return classes.conversation;
		else if (path === FrontendRoutes.BALLROOM())
			return classes.ballroom;
		else if (path === FrontendRoutes.HOME)
			return 'home';
		else if (path === FrontendRoutes.SOCIAL_WALL)
			return classes.socialwall;
		else if (path === FrontendRoutes.PHOTO_BOOTH)
			return classes.photobooth;
		else if (path === FrontendRoutes.PROGRAMME)
			return classes.programme;
		else if (path === FrontendRoutes.LOGOUT || path === FrontendRoutes.UNAUTHORIZED || path === FrontendRoutes.AFTER_EVENT_MESSAGE)
			return classes.logout;
		else return '';
	};

	const handleResize = () => {
		if (window.innerHeight > window.innerWidth) {
			setLandScapeMode(false);
		} else {
			setLandScapeMode(true);
		}
	};

	useEffect(() => {
		const image = new Image();
		image.onload = function () {
			setImageLoading(false);
		};
		image.src = getBgUrl(document.getElementById('main-div'));
		handleResize();
		window.addEventListener('resize', handleResize);
	}, []);
	console.log(!isSpecial, landScapeMode, !imgLoading)

	return (<PlayerLoadingWrapper loading={authLoading}>
		<div
			className={'App ' + classes.root + ' ' + backgroundClass()}
			id={'main-div'}
		>
			{landScapeMode && <AppHeader isSpecial={isSpecial}/>}
			{landScapeMode && !path.includes(FrontendRoutes.HOME) && !isSpecial &&
			user && <ProgramHeader user={user}/>}
			{
				landScapeMode ?
					<Switch>
						{/*<PrivateRoute exact={true} path={FrontendRoutes.COUNTDOWN} component={CountdownPage}/>*/}
						<Route exact path={FrontendRoutes.AFTER_EVENT_MESSAGE} component={AfterEventMessage}/>
						{/*<Route exact path={FrontendRoutes.UNAUTHORIZED} component={UnauthorizedPage}/>*/}
						<PrivateRoute user={user} exact path={FrontendRoutes.HOME} component={Homepage}/>
						<PrivateRoute user={user} exact path={FrontendRoutes.PROGRAMME} component={ProgramPage}/>
						<PrivateRoute user={user}
													exact
													path={FrontendRoutes.BALLROOM()}
													render={() => <BallroomPage
														user={user}
														users={users}
														onlineUsers={onlineUsers}/>}/>
						<PrivateRoute exact
													user={user}
													path={FrontendRoutes.CONVERSATION}
													render={() => <ConversationPage user={user}
																													users={users}
																													onlineUsers={onlineUsers}/>}/>
						<PrivateRoute exact user={user} path={FrontendRoutes.PHOTO_BOOTH} component={PhotoBoothPage}/>
						<PrivateRoute exact
													user={user}
													path={FrontendRoutes.SOCIAL_WALL}
													render={() => <SocialWallPage users={users}/>}/>
						{config.SHOW_FEEDBACK && <PrivateRoute exact={true} user={user} path={FrontendRoutes.FEEDBACK}
																									 component={FeedbackPage}/>}
						<Route exact={true} user={user} path={FrontendRoutes.FAQ} component={FAQPage}/>
						<Route exact={true} user={user} path={FrontendRoutes.LOGOUT} component={LogoutPage}/>
						<PrivateRoute exact={true}
													user={user}
													path={FrontendRoutes.SOCIAL_WALL_MODERATION}
													render={() => <SocialWallModerationPage users={users}/>}/>
						<PrivateRoute exact={true}
													user={user}
													path={FrontendRoutes.QA_MODERATION}
													render={() => <QAModerationPage users={users}/>}/>
						<PrivateRoute exact={true}
													user={user}
													path={FrontendRoutes.REPORTS}
													component={ReportsPage}/>
						<Redirect to={FrontendRoutes.HOME}/>
					</Switch>
					: <PortraitWarning/>
			}
			{!isSpecial && landScapeMode && !imgLoading && <AppFooter hideStatus={hideStatus}
																																setHideStatus={handleHideStatus}
																																users={users}
																																user={user}
																																onlineUsers={onlineUsers}
																																path={path}/>}
			{/*<DebugLog/>*/}
		</div>
	</PlayerLoadingWrapper>);
};

export default App;
